/* eslint-disable camelcase */
// These LiveAnswersMessage types attempt to massage
// the data that is currently being shimmed into the
// inline data via stripe_assistant - live_answers_v2.
// They're mostly the same shape, but with slight tweaks
// to make them easier to work with.

import {
  ClientResponse,
  StripeAssistantLiveAnswersSource,
  ToolCall,
  InlineDataConversationMessage,
  Client,
  StripeAssistantLiveAnswersResponse,
} from './types';

export interface LiveAnswersMessageData {
  streamId: string;
  content: string;
  is_complete: boolean;
  was_halted: boolean;
  not_answerable: boolean;
  tool_calls?: ToolCall[];
  is_long_running?: boolean;
  is_preparing_response?: boolean;
  progress_update?: string | null;
  structured_content?: string;
}

export interface LiveAnswersStreamingCompletionData {
  content: string;
  toolCalls?: ToolCall[];
  answerType?: 'unanswerable' | 'answerable';
  sources?: StripeAssistantLiveAnswersSource[];
  structuredContent?: string;
  wasHalted?: boolean;
}

export interface LiveAnswersMessage {
  conversationId: string;
  saResponseId: string;
  messageId: string;
  streamId: string;
  cacheId: string;
  // If the message does not have data, it indicates
  // that it the stream was created, but has not
  // yet been streamed.
  data?: LiveAnswersMessageData;
  sources: StripeAssistantLiveAnswersSource[];
}

export type LiveAnswersMessageResponseData =
  | {
      type: 'error';
      error: string;
    }
  | {
      type: 'success';
      next_offset: number;
      message: LiveAnswersMessage & {
        data: LiveAnswersMessageData;
      };
    };

export type LiveAnswersMessageResponse = ClientResponse & {
  data?: LiveAnswersMessageResponseData;
};

export interface LiveAnswersClient {
  fetchMessage: (
    message: LiveAnswersMessage,
    options?: {
      offset?: number;
      abortSignal?: AbortSignal;
    },
  ) => Promise<LiveAnswersMessageResponse>;
}

// Returns a simplified LiveAnswersClient that can be used to fetch
// and poll Live Answers messages and interact with the data in a
// consistent way.
// Additionally, returns a LiveAnswersMessage, which is a normalized
// message shape for Live Answers messages instead of needing to interact
// with the inline data directly.
export function buildLiveAnswersClient({
  client,
  conversationId,
  message,
}: {
  client: Client;
  conversationId: string;
  message: InlineDataConversationMessage;
}): {
  liveAnswersMessage: LiveAnswersMessage;
  liveAnswersClient: LiveAnswersClient;
} {
  const {inlineData} = message;
  if (inlineData.name !== 'stripe_assistant') {
    throw new Error(
      `Attempted to build Live Answers client for unsupported inline data - ${inlineData.name}`,
    );
  }

  const inlineDataType =
    'type' in inlineData.data ? inlineData.data.type : null;
  if (
    !inlineDataType ||
    !['live_answers_v2', 'fully_async'].includes(inlineDataType)
  ) {
    throw new Error(
      `Attempted to build Live Answers client for unsupported inline data type - ${inlineDataType}`,
    );
  }

  const initialResponseData =
    inlineData.data as unknown as StripeAssistantLiveAnswersResponse;

  let data: LiveAnswersMessageData | undefined;

  if (
    initialResponseData.is_streamed &&
    (initialResponseData.content ||
      initialResponseData.structured_content ||
      initialResponseData.was_halted)
  ) {
    data = {
      streamId: initialResponseData.llm_conversation_id,
      content: initialResponseData.content ?? '',
      is_complete: true,
      was_halted: initialResponseData.was_halted || false,
      not_answerable: false,
      tool_calls:
        initialResponseData.tool_calls ?? initialResponseData.agent_calls,
      structured_content: initialResponseData.structured_content,
    };
  }

  const liveAnswersMessage: LiveAnswersMessage = {
    conversationId,
    saResponseId: initialResponseData.sa_response_id,
    messageId: message.id,
    streamId: initialResponseData.llm_conversation_id,
    cacheId: initialResponseData.cache_uuid,
    data,
    sources: initialResponseData.sources ?? [],
  };

  const liveAnswersClient: LiveAnswersClient = {
    fetchMessage: async (
      message: LiveAnswersMessage,
      options?: {
        offset?: number;
        abortSignal?: AbortSignal;
      },
    ): Promise<LiveAnswersMessageResponse> => {
      const {conversationId, saResponseId, messageId, streamId, cacheId} =
        message;

      const offset = options?.offset ?? 0;
      const signal = options?.abortSignal ?? new AbortController().signal;

      const resp = await client.streamText({
        conversationId,
        saResponseId,
        effectId: messageId,
        streamId,
        offset,
        cacheUuid: cacheId,
        signal,
      });

      const {data: respData} = resp;

      if (resp.code >= 400) {
        const error =
          respData?.type === 'error'
            ? respData.error
            : 'An unknown error occurred fetching live answers message';
        return {
          ...resp,
          data: {
            type: 'error',
            error,
          },
        };
      }

      if (!respData) {
        throw new Error('Server did not return Live Answers message data');
      }

      if (respData.type === 'error') {
        return {
          ...resp,
          data: respData,
        };
      }

      const {
        content,
        is_complete,
        was_halted,
        not_answerable,
        agent_calls: tool_calls,
        next_offset,
        is_long_running,
        status,
        progress_update,
        sources,
        structured_content,
      } = respData;

      const respMessageData: LiveAnswersMessageData = {
        streamId,
        content,
        is_complete,
        was_halted,
        not_answerable,
        tool_calls,
        is_long_running,
        is_preparing_response: status === 'preparing_response',
        progress_update,
        structured_content: structured_content || undefined,
      };

      // normalizing the shape of the data to deal with inconsistency in the
      // api responses. this is all going away in api v2.
      let respSources: StripeAssistantLiveAnswersSource[];
      // message here refers to the passed parameter, which should be the original message, but
      // should represent the most up to date message.
      if (message.sources && message.sources.length > 0) {
        respSources = message.sources;
      } else if (sources && sources.length > 0) {
        respSources = sources;
      } else {
        // fallback to the original message's sources
        respSources = liveAnswersMessage.sources;
      }

      return {
        ...resp,
        data: {
          type: 'success',
          next_offset,
          message: {
            ...liveAnswersMessage,
            data: respMessageData,
            sources: respSources,
          },
        },
      };
    },
  };

  return {
    liveAnswersMessage,
    liveAnswersClient,
  };
}
